import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import portalT from '../translations/portal';
import axios from 'axios';
import { ArrowLeft, GraduationCap, Loader } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import ExportPDFButton from '../components/ExportPDFButton';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const VirtualTraining = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { lang } = useLanguage();
  const t = portalT[lang];
  const tp = t.toolPages.virtualTraining;

  const [formData, setFormData] = useState({
    topic: '', skill_level: 'intermediate', learning_objectives: '',
    duration: '4 hours', team_size: '1-10',
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/tools/virtual-training`, formData, { headers: { Authorization: `Bearer ${token}` } });
      setResult(res.data);
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally { setLoading(false); }
  };

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const durationOpts = lang === 'nl'
    ? [['2 hours','2 uur'],['4 hours','4 uur (halve dag)'],['1 day','1 dag'],['2 days','2 dagen'],['1 week','1 week'],['2 weeks','2 weken']]
    : [['2 hours','2 hours'],['4 hours','4 hours (Half day)'],['1 day','1 day (Full day)'],['2 days','2 days'],['1 week','1 week'],['2 weeks','2 weeks']];

  const teamOpts = lang === 'nl'
    ? [['1-10','1-10 personen'],['11-25','11-25 personen'],['26-50','26-50 personen'],['50+','50+ personen']]
    : [['1-10','1-10 people'],['11-25','11-25 people'],['26-50','26-50 people'],['50+','50+ people']];

  return (
    <div className="tool-page">
      <div className="tool-header">
        <button onClick={() => navigate('/portal/ai-tools')} className="btn-secondary" data-testid="back-btn"><ArrowLeft size={16} /> {t.common.aiTools}</button>
        <h1 className="heading-1" data-testid="training-title">{tp.title}</h1>
      </div>
      <div className="tool-content">
        {!result ? (
          <div className="tool-form-container">
            <div className="tool-intro"><GraduationCap size={32} /><h2 className="heading-2">{tp.intro}</h2><p className="body-medium text-secondary">{tp.introDesc}</p></div>
            <form onSubmit={handleSubmit} className="tool-form">
              <div className="form-group"><label className="body-small">{tp.topic}</label><input type="text" name="topic" value={formData.topic} onChange={handleChange} required className="form-input" placeholder={tp.topicPlaceholder} data-testid="input-topic" /></div>
              <div className="form-group"><label className="body-small">{tp.skillLevel}</label><select name="skill_level" value={formData.skill_level} onChange={handleChange} className="form-input" data-testid="select-skill"><option value="beginner">{tp.beginner}</option><option value="intermediate">{tp.intermediate}</option><option value="advanced">{tp.advanced}</option></select></div>
              <div className="form-group"><label className="body-small">{tp.objectives}</label><textarea name="learning_objectives" value={formData.learning_objectives} onChange={handleChange} required rows="5" className="form-input" placeholder={tp.objectivesPlaceholder} data-testid="input-objectives" /></div>
              <div className="form-row">
                <div className="form-group"><label className="body-small">{tp.duration}</label><select name="duration" value={formData.duration} onChange={handleChange} className="form-input" data-testid="select-duration">{(Array.isArray(durationOpts) ? durationOpts : []).map(([v,l]) => <option key={v} value={v}>{l}</option>)}</select></div>
                <div className="form-group"><label className="body-small">{tp.teamSize}</label><select name="team_size" value={formData.team_size} onChange={handleChange} className="form-input" data-testid="select-team">{(Array.isArray(teamOpts) ? teamOpts : []).map(([v,l]) => <option key={v} value={v}>{l}</option>)}</select></div>
              </div>
              <button type="submit" disabled={loading} className="btn-primary submit-btn" data-testid="submit-btn">{loading ? <><Loader className="spin" size={16} /> {tp.generating}</> : <><GraduationCap size={16} /> {tp.generate}</>}</button>
            </form>
          </div>
        ) : (
          <div className="tool-result">
            <div className="result-header"><h2 className="heading-2">{tp.resultTitle}</h2><div style={{display:'flex',gap:'0.5rem'}}><ExportPDFButton title={tp.resultTitle} content={result.training_program} toolName={tp.title} /><button onClick={() => setResult(null)} className="btn-secondary" data-testid="create-another-btn">{tp.createAnother}</button></div></div>
            <div className="result-content markdown-content"><ReactMarkdown>{result.training_program}</ReactMarkdown></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VirtualTraining;
